import {
  type CSSVariablesResolver,
  createTheme,
  defaultVariantColorsResolver,
  type MantineTheme,
  type VariantColorsResolver,
} from "@mantine/core";
import { palette } from "@/styles/tokens/palette.generated";
import { renderDropdownOption } from "./theme-dropdown-option";

/**
 * The shade every non-primary color's TEXT is re-pointed to (see
 * `contrastSafeColorVariables`). Shade 7 clears WCAG AA (≥4.5:1) as text on
 * white for all scales — the lightest shade that does so for neutral.
 */
const CONTRAST_SAFE_TEXT_SHADE = 7;

/**
 * `primaryShade` is 4 so filled fills render Vibrant Yellow (primary is now a
 * dedicated yellow ramp anchored at index 4). But in Mantine's light scheme
 * `primaryShade` ALSO drives:
 *   - `variant="light"|"subtle"|"transparent"` label text
 *   - `variant="outline"` text + border
 *   - `variant="filled"` background
 *   - `c="<color>"` text
 * …landing every non-primary color on shade 4 by default — failing WCAG for
 * scales whose shade 4 isn't dark enough as text on white.
 *
 * Two levers fix this centrally, so no call site changes:
 *   1. `variantColorResolver` (below) — pins the primary/brand light+subtle
 *      surfaces to neutral, and pins non-primary FILLED fills to a dark shade
 *      with white text (a filled label can't be fixed via CSS vars because its
 *      color is white/autoContrast, not a `-*-color` token).
 *   2. `contrastSafeColorVariables` — re-points the `-text`, `-light-color`,
 *      and `-outline` tokens of every non-primary color to shade 7.
 */
const variantColorResolver: VariantColorsResolver = (input) => {
  const resolved = defaultVariantColorsResolver(input);
  // An undefined color resolves to `primary` (the default primaryColor).
  const isPrimary = !input.color || input.color === "primary" || input.color === "brand";

  if (isPrimary) {
    if (input.variant === "subtle" || input.variant === "transparent") {
      return { ...resolved, color: "var(--color-neutral-7)" };
    }
    if (input.variant === "light") {
      return {
        ...resolved,
        color: "var(--color-neutral-7)",
        background: "var(--color-neutral-2)",
        hover: "var(--color-neutral-3)",
      };
    }
    return resolved;
  }

  // `input.color` is narrowed to defined here — isPrimary already caught falsy.
  if (!input.color) return resolved;

  // Warning hues: Mantine's yellow/orange never reach AA as text on white, and
  // there's no design-system warning ramp. Render them as the brand Vibrant
  // Yellow (primary.4 — the primaryShade anchor) chip with dark text — legible
  // and on-brand — for every variant. Explicit shades (`color="yellow.8"`) opt out.
  const isWarning = input.color === "yellow" || input.color === "orange";
  if (isWarning) {
    return {
      ...resolved,
      background: "var(--mantine-color-primary-4)",
      hover: "var(--mantine-color-primary-5)",
      color: "var(--color-neutral-9)",
      border: "1px solid transparent",
    };
  }

  // Non-primary filled fill lands on shade 2 (near-white) with a white label —
  // unreadable. Pin to a dark shade + white text (standard solid-chip look).
  // Skip explicit-shade colors like `color="red.6"` (caller chose the shade).
  const hasExplicitShade = input.color.includes(".");
  if (input.variant === "filled" && !hasExplicitShade) {
    return {
      ...resolved,
      background: `var(--mantine-color-${input.color}-${CONTRAST_SAFE_TEXT_SHADE})`,
      hover: `var(--mantine-color-${input.color}-${CONTRAST_SAFE_TEXT_SHADE + 1})`,
      color: "var(--mantine-color-white)",
    };
  }
  return resolved;
};

/**
 * Re-point non-primary text tokens off shade 2 (see `variantColorResolver`).
 * `primary`/`brand` are skipped: they keep the Vibrant-Yellow-driven defaults
 * and are neutralized for text by `variantColorResolver`. Applies to the built-in
 * Mantine names (blue/green/red/gray/…) used across the app as well as the custom
 * scales, since every color inherits the same shade-2 text via `primaryShade`.
 */
export const contrastSafeColorVariables: CSSVariablesResolver = (mantineTheme) => {
  const light: Record<string, string> = {};
  for (const name of Object.keys(mantineTheme.colors)) {
    if (name === "primary" || name === "brand") continue;
    const darkText = `var(--mantine-color-${name}-${CONTRAST_SAFE_TEXT_SHADE})`;
    light[`--mantine-color-${name}-text`] = darkText; // c="<color>"
    light[`--mantine-color-${name}-light-color`] = darkText; // light / subtle / transparent / white text
    light[`--mantine-color-${name}-outline`] = darkText; // outline text + border
  }
  // Link text is accent blue, never primary yellow. Mantine points the anchor color at
  // primary.<primaryShade> (Vibrant Yellow), so every un-colored <Anchor> rendered yellow.
  light["--mantine-color-anchor"] = "var(--color-secondary-5)";
  return { variables: {}, light, dark: {} };
};

export const theme = createTheme({
  variantColorResolver,
  // Color-scheme — design-system scales (generated from styles/tokens/palette.source.ts).
  colors: {
    primary: palette.primary,
    secondary: palette.secondary,
    neutral: palette.neutral,
    success: palette.success,
    error: palette.error,
    // Legacy alias: keeps existing `color="brand"` references resolving during the
    // migration. Removed once those refs are codemodded to `color="primary"`.
    brand: palette.primary,
    // Alias the saturated built-in Mantine status hues onto the design-system
    // ramps. Mantine's stock blue/green/red are tuned for white-text-on-fill and
    // never reach WCAG AA (4.5:1) as TEXT on white at ANY shade (its green tops
    // out ~2.8:1), so `color="green"|"blue"|"red"` status pills/labels can't be
    // made legible without this. The design-system ramps clear AA at shade 7
    // (see contrastSafeColorVariables). Mantine `gray` already passes at 7 and is
    // left as-is; `yellow`/`orange` warning hues still need a dedicated treatment.
    blue: palette.secondary,
    green: palette.success,
    red: palette.error,
  },
  primaryColor: "primary",
  // The single "primary" shade for every filled fill and accent — buttons,
  // badges, switches, tabs, loaders, progress, focus rings — is Vibrant Yellow
  // (primary.4). Yellow is light/low-contrast, so filled Badges pin dark
  // (neutral-9) label text via the Badge override below; loader/progress/tab
  // accents are acceptable as-is.
  primaryShade: 4,
  white: palette.white,
  black: palette.black,

  // Spacing and radius.
  defaultRadius: "4px",

  // Corner radius scale. Mirrors styles/spacing.scss ($radius-*); emits the
  // --mantine-radius-* props.
  radius: {
    xs: "2px",
    sm: "4px",
    md: "8px",
    lg: "12px",
    xl: "16px",
    xxl: "32px",
    full: "999px",
  },

  // Typography.
  fontFamily: "var(--font-family-primary)",

  // Mantine `size=` keys point at design tokens. Keys preserve their current
  // rendered px (so existing `size=` props on Text/Button/etc. don't shift),
  // which is why Mantine "xl"→--font-xl (18) and "xxl"→--font-xxl (20). The two
  // off-scale keys fold per the migration: xxs 10→11 (--font-xs), lg 15→14 (--font-md).
  fontSizes: {
    xxs: "var(--font-xs)",
    xs: "var(--font-xs)",
    sm: "var(--font-sm)",
    md: "var(--font-md)",
    lg: "var(--font-md)",
    xl: "var(--font-xl)",
    xxl: "var(--font-xxl)",
  },

  headings: {
    fontFamily: "var(--font-family-primary)",
    fontWeight: "var(--fw-medium)",
    sizes: {
      h1: { fontSize: "var(--font-3xl)" }, // 24
      h2: { fontSize: "var(--font-xxl)" }, // 21 -> 20
      h3: { fontSize: "var(--font-md)", fontWeight: "var(--fw-medium)" }, // 15 -> 14, medium (500)
      h4: { fontSize: "var(--font-md)", fontWeight: "var(--fw-semibold)" }, // 14, semibold (600)
      h5: { fontSize: "var(--font-sm)", fontWeight: "var(--fw-semibold)" }, // 12, semibold (600)
      h6: { fontSize: "var(--font-xs)" }, // 10 -> 11
    },
  },

  // Single spacing scale — margins, padding, and gaps. Mirrors
  // styles/spacing.scss ($spacing-*); emits the --mantine-spacing-* props.
  spacing: {
    "3xs": "2px",
    xxs: "4px",
    xs: "8px",
    sm: "12px",
    md: "16px",
    lg: "20px",
    xl: "24px",
    xxl: "32px",
    "3xl": "40px",
    "4xl": "56px",
  },

  // Component overrides.
  components: {
    ActionIcon: {
      defaultProps: {
        size: "lg",
        variant: "default",
      },
    },

    Badge: {
      // primaryShade is Vibrant Yellow (light), so a default/primary *filled*
      // Badge fills yellow — pin its label to dark neutral-9 for contrast.
      // Colored badges (red, etc.) and non-filled variants keep their own text.
      styles: (_theme: MantineTheme, props: { color?: string; variant?: string }) => {
        const isPrimary = !props.color || props.color === "primary" || props.color === "brand";
        const isFilled = !props.variant || props.variant === "filled";
        if (isPrimary && isFilled) {
          return { label: { color: "var(--color-neutral-9)" } };
        }
        return {};
      },
    },

    TextInput: {
      // DS 2.0 default size (md = 36px). Previously relied on Mantine's implicit "sm".
      defaultProps: { size: "md" },
      styles: {
        input: {
          fontSize: "var(--font-md)",
        },
      },
    },

    Title: {
      defaultProps: { order: 2, fontFamily: "var(--font-family-primary)" },
    },

    Modal: {
      styles: {
        title: {
          fontWeight: "var(--fw-bold)",
        },
      },
    },

    // DS 2.0 default size (md = 36px) for every input primitive, matching
    // TextInput above. Without it Mantine falls back to its implicit "sm" (32px),
    // so an un-sized Select/Textarea/etc. would render 4px shorter than a sibling
    // TextInput on the same form. The per-size SCSS rules in styles/theme/* treat
    // md as the authoritative height; this makes it the default.
    Select: {
      // DS: FontAwesome "check" on the trailing edge of the selected option
      // (renderDropdownOption replaces Mantine's built-in CheckIcon so every
      // dropdown reads the same as our Menu-based selectors).
      defaultProps: { size: "md", renderOption: renderDropdownOption },
      styles: {
        input: {
          fontSize: "var(--font-md)",
        },
        option: {
          fontSize: "var(--font-md)",
        },
      },
    },

    MultiSelect: {
      // DS: FontAwesome "check" on the trailing edge of the selected option.
      defaultProps: { size: "md", renderOption: renderDropdownOption },
    },

    Textarea: { defaultProps: { size: "md" } },
    NumberInput: { defaultProps: { size: "md" } },
    Autocomplete: { defaultProps: { size: "md" } },
    FileInput: { defaultProps: { size: "md" } },
    PillsInput: { defaultProps: { size: "md" } },
    DateInput: { defaultProps: { size: "md" } },
    TimeInput: { defaultProps: { size: "md" } },
    InputBase: { defaultProps: { size: "md" } },

    Button: {
      defaultProps: {
        autoContrast: true,
      },
    },

    Pagination: {
      defaultProps: {
        size: "sm",
        radius: "sm",
        siblings: 1,
        boundaries: 1,
        withEdges: true,
        withControls: false,
        color: palette.neutral[7],
        autoContrast: true,
        gap: "xs",
      },
      styles: {
        root: {
          fontFamily: "var(--font-family-primary)",
          fontWeight: "var(--fw-regular)",
        },
      },
    },
  },
});
